import React, { useState } from 'react';
import { CoverLetterResponse } from '@/api/types';
import { pdf, Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';

import { CoverLetterHeader as LetterHeader } from './CoverLetterHeader';
import { CoverLetterBody as LetterBody } from './CoverLetterBody';

interface CoverLetterPanelProps {
  result: CoverLetterResponse;
  onSave: () => void;
  isSaving: boolean;
  onRetry?: () => void;
}

const styles = StyleSheet.create({
  page: {
    padding: 72,
    fontFamily: 'Times-Roman',
    fontSize: 11,
    lineHeight: 1.5,
  },
  paragraph: {
    marginBottom: 11,
    textAlign: 'justify'
  },
  text: {
    fontFamily: 'Times-Roman'
  },
  bold: {
    fontFamily: 'Times-Bold'
  }
});

function parseBoldTextReactPdf(text: string) {
  const parts = text.split(/(\*\*[\s\S]*?\*\*)/g);
  return parts.map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      const boldText = part.substring(2, part.length - 2);
      return <Text key={index} style={styles.bold}>{boldText}</Text>;
    }
    return <Text key={index} style={styles.text}>{part}</Text>;
  });
}

const CoverLetterPDF = ({ content }: { content: string }) => {
  const paragraphs = content.split('\n');
  return (
    <Document>
      <Page size="LETTER" style={styles.page}>
        <View>
          {paragraphs.map((p, pIdx) => {
            if (!p.trim()) return <Text key={pIdx} style={{ fontSize: 4 }}>{'\n'}</Text>;
            return (
              <Text key={pIdx} style={styles.paragraph}>
                {parseBoldTextReactPdf(p)}
              </Text>
            );
          })}
        </View>
      </Page>
    </Document>
  );
};

export function CoverLetterPanel({ result, onSave, isSaving, onRetry }: CoverLetterPanelProps) {
  const [content, setContent] = useState(result.markdown.replace(/\n{2,}/g, '\n'));
  const [copied, setCopied] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleExportPDF = async () => {
    setIsDownloading(true);
    try {
      const blob = await pdf(<CoverLetterPDF content={content} />).toBlob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'cover_letter.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {} finally {
      setIsDownloading(false);
    }
  };

  const insertBold = (e: React.MouseEvent) => {
    e.preventDefault();
    document.execCommand('bold', false, undefined);
  };

  return (
    <div className="flex flex-col flex-1 bg-white dark:bg-slate-900 relative rounded-xl border border-slate-200 dark:border-slate-800 h-full overflow-hidden dark:shadow-sm min-h-0">
      <LetterHeader
        onRetry={onRetry}
        handleCopy={handleCopy}
        copied={copied}
        handleExportPDF={handleExportPDF}
        isDownloading={isDownloading}
      />
      <LetterBody
        content={content}
        setContent={setContent}
        files={result.files}
        insertBold={insertBold}
      />
    </div>
  );
}

